import React from 'react'
import { useTranslation } from 'react-i18next'
import { InsightData } from 'schemas/action/InsightDataSchema'
import MetricCategoryItem, { MetricCategoryItemStrings } from 'enums/MetricCategoryItem'
import { MetricItem } from 'enums/MetricItem'
import { useDisplayTheme } from 'hooks/useUiState'
// @ts-ignore
import { SimpleBar } from '@netbasequid/chart'
// @ts-ignore
import { getValue } from 'utils/metricCategoryItemUtil'
import { InsightChartProps, SideBySideSimplifiedData, SideBySideSelectionType } from '../../types'
import {
  getDisplayInsightValue,
  getDisplayAvg,
  getSurveyScoreName,
  getSortedSetBreakInfo,
  getPercentageByIndexandBreakInfo
} from '../utils'

import {
  InsightChartContainer,
  InsightContainer,
  CountContainer,
  LabelContainer,
  LabelName,
  LabelNameWithTooltip,
  LabelValue
} from './styles'

const SURVEY_SCORE_COLOR = [
  'metric.surveyScore.tenScore',
  'metric.surveyScore.highScore',
  'metric.surveyScore.mediumScore',
  'metric.surveyScore.lowScore',
  'metric.surveyScore.noScore',
  'metric.surveyScore.noResponseSurvey'
]

const getSetInfo = (data: SideBySideSimplifiedData) => {
  const { set, topicId, topicName, themeId, themeName, tagName, docName } = data
  let total = 0
  let sum = 0

  const setInfoMap: Record<
    MetricCategoryItemStrings,
    SideBySideSelectionType
  > = MetricItem.CHILDREN_SURVEY_SCORE.reduce(
    (acc, cur) => ({
      ...acc,
      [cur]: {
        name: cur,
        value: 0,
        insightType: 'SurveyScore',
        percentage: 0,
        insight: { name: cur } as InsightData,
        topicId,
        topicName,
        themeId,
        themeName,
        tagName,
        docName
      }
    }),
    {} as Record<MetricCategoryItemStrings, SideBySideSelectionType>
  )

  set.forEach((insightData) => {
    const name = insightData.name! as MetricCategoryItemStrings
    if (insightData.name! !== MetricCategoryItem.NO_RESPONSE_SURVEY) {
      total += insightData.value!
      sum += insightData.value! * getValue(name)
    }

    switch (name) {
      case MetricCategoryItem.TEN_SCORE:
      case MetricCategoryItem.NO_SCORE:
      case MetricCategoryItem.NO_RESPONSE_SURVEY:
        setInfoMap[name].value = insightData.value!
        break
      case MetricCategoryItem.NINE_SCORE:
      case MetricCategoryItem.EIGHT_SCORE:
      case MetricCategoryItem.SEVEN_SCORE:
        setInfoMap[MetricCategoryItem.HIGH_SCORE].value += insightData.value!
        break
      case MetricCategoryItem.SIX_SCORE:
      case MetricCategoryItem.FIVE_SCORE:
      case MetricCategoryItem.FOUR_SCORE:
        setInfoMap[MetricCategoryItem.MEDIUM_SCORE].value += insightData.value!
        break
      case MetricCategoryItem.THREE_SCORE:
      case MetricCategoryItem.TWO_SCORE:
      case MetricCategoryItem.ONE_SCORE:
        setInfoMap[MetricCategoryItem.LOW_SCORE].value += insightData.value!
        break
      default:
        break
    }
  })

  const sortedSet = Object.values(setInfoMap).sort((a, b) => b.value! - a.value!)

  const breakInfo = getSortedSetBreakInfo(
    (sortedSet as unknown) as InsightChartProps['data']['set']
  )

  sortedSet.forEach((insightData, idx) => {
    const name = insightData.name! as MetricCategoryItemStrings
    const percentage = getPercentageByIndexandBreakInfo(
      {
        value: insightData.value!,
        totalBars: sortedSet.length,
        index: idx
      },
      breakInfo
    )
    setInfoMap[name].percentage = percentage
    setInfoMap[name].name = name
  })

  const setInfos = MetricItem.CHILDREN_SURVEY_SCORE.map(
    (starRatingName: MetricCategoryItemStrings) => setInfoMap[starRatingName]
  )

  return {
    hasBreakSymbol: breakInfo.hasBreakSymbol,
    setInfos,
    total,
    avg: sum / total
  }
}

const SurveyScoreChart = ({ data, onCellClick }: InsightChartProps) => {
  const theme = useDisplayTheme()
  const { t } = useTranslation()

  if (!data) return null
  const { hasBreakSymbol, setInfos, avg, total } = getSetInfo(data)

  return (
    <InsightChartContainer>
      {setInfos?.map((insightData, idx) => (
        <InsightContainer key={insightData.name} mode={theme} clickable={insightData.value > 0}>
          <LabelContainer onClick={() => onCellClick(insightData)}>
            <LabelName>
              {getSurveyScoreName(insightData.name as MetricCategoryItemStrings)}
            </LabelName>
            <LabelValue>{getDisplayInsightValue(insightData.value)}</LabelValue>
          </LabelContainer>
          <SimpleBar
            progress={insightData.percentage}
            colorSetting={SURVEY_SCORE_COLOR[idx]}
            mode={theme}
            breakSymbolType="BreakDoubleSlash100"
            breakSymbolPosition={hasBreakSymbol ? 70 : undefined}
            breakSymbolTooltipContent={t('breakSymbolTooltip')}
            height="2px"
          />
        </InsightContainer>
      ))}
      <CountContainer displayType="average">
        <LabelContainer>
          <LabelNameWithTooltip type="weak">{t('average')}:</LabelNameWithTooltip>
          <LabelValue type="weak">{getDisplayAvg(avg)}</LabelValue>
        </LabelContainer>
      </CountContainer>

      <CountContainer displayType="total">
        <LabelContainer>
          <LabelNameWithTooltip type="weak">{t('totalResponse')}:</LabelNameWithTooltip>
          <LabelValue type="weak">{getDisplayInsightValue(total)}</LabelValue>
        </LabelContainer>
      </CountContainer>
    </InsightChartContainer>
  )
}
export default SurveyScoreChart
